import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Edit from '@material-ui/icons/Edit';
import Delete from '@material-ui/icons/Delete';
import { raiseToast } from '$components/Toast';
import checkGradeReleased from '$utils/checkGradesReleased';
import getExternalLinkProperties from '$utils/getExternalLinkProperties';

import {
  Wrapper,
  Title,
  Decorations,
  RightDecorations,
  LeftDecorations,
  GradeType,
  GradeTypeValue,
} from './AssignmentDetails.styled';
import ProviderLogo from '$components/ProviderLogo';
import CustomProviderLogo from '$components/CustomProviderLogo';
import GradesReleased from '$components/GradesReleased';
import ExternalLink from '$components/ExternalLink';
import ManualAssignmentModal from '$components/ManualAssignmentModal';
import ContextMenu from '$components/ContextMenu';
import UploadGrades from '$components/UploadGrades';

import {
  Assignment as AssignmentType,
  GradeType as GradesPropType,
} from '$variables/types';
import { providers, gradeTypeTitles, EDUCATOR } from '$variables/constants';
import Pill from '$components/Pill';
import * as TYPES from '$variables/types';

const { MANUAL, HAND_IN, QUIZZES, LTI_1P3 } = providers;

const AssignmentDetails = ({
  assignment,
  launchTurnitinAssignment,
  launchTurnitinSubmission,
  updateAssignment,
  isUpdatingAssignment,
  deleteAssignment,
  spaceId,
  importManualAssignmentGrades,
  grades,
  isUploadingGrades,
  viewAs,
  ltiProvider,
}) => {
  const {
    id,
    title,
    provider,
    externalId,
    gradesReleasedAt,
    space,
    gradeType,
    submission,
    studentGrade,
    endDate,
  } = assignment;

  const isEducator = viewAs === EDUCATOR;

  const isHandinAssignment = provider === HAND_IN;
  const isManualAssignment = provider === MANUAL;
  const isLti1p3Assignment = provider === LTI_1P3;

  const [manualAssignmentModalOpen, setManualAssignmentModalOpen] =
    useState(false);

  const history = useHistory();

  const contextMenuItems = [
    {
      title: 'Edit',
      Icon: Edit,
      onClick: () => setManualAssignmentModalOpen(true),
      divider: true,
    },
    {
      title: 'Delete',
      Icon: Delete,
      onClick: async () => {
        try {
          await deleteAssignment({ assignmentId: assignment.id });
          raiseToast({ message: 'Assignment deleted!' });
          history.push(`/${spaceId}`);
        } catch (_) {
          raiseToast({ message: 'Oops! Something went wrong.', type: 'fail' });
        }
      },
      confirmation: {
        title: 'Delete assignment',
        message: 'Are you sure you want to delete this assignment?',
        submitText: 'Delete',
      },
    },
  ];

  const hasGradesReleased = checkGradeReleased(assignment);

  const showAssignmentManagementControls = isManualAssignment && isEducator;
  const showExternalLink = isEducator
    ? !isManualAssignment
    : isHandinAssignment;
  const showGradesReleased = provider !== QUIZZES;

  return (
    <Wrapper data-testid="assignment-details">
      <Decorations>
        <LeftDecorations>
          {isLti1p3Assignment ? (
            <CustomProviderLogo
              iconUrl={ltiProvider.iconUrl}
              description={ltiProvider.description}
              size="medium"
            />
          ) : (
            <ProviderLogo provider={provider} size="medium" />
          )}
          {isManualAssignment && <Pill look="grey">Manual assignment</Pill>}
          {showGradesReleased && (
            <GradesReleased gradesReleasedAt={gradesReleasedAt} />
          )}
        </LeftDecorations>
        <RightDecorations>
          {showExternalLink && (
            <ExternalLink
              // look="green"
              {...getExternalLinkProperties({
                space,
                externalId,
                id,
                provider,
                studentGrade,
                isEducator,
                launchTurnitinAssignment,
                launchTurnitinSubmission,
                submission,
                endDate,
              })}
            />
          )}
          {showAssignmentManagementControls && (
            <>
              <UploadGrades
                assignmentId={id}
                uploadGrades={importManualAssignmentGrades}
                grades={grades}
                gradeType={gradeType}
                isUploading={isUploadingGrades}
                hasGradesReleased={hasGradesReleased}
                disabled={!isEducator}
              />
              <ContextMenu menuItems={contextMenuItems} />
            </>
          )}
        </RightDecorations>
      </Decorations>
      <Title>{title}</Title>
      {gradeType && (
        <GradeType>
          Grade type:
          <GradeTypeValue>{gradeTypeTitles[gradeType]}</GradeTypeValue>
        </GradeType>
      )}
      {isManualAssignment && (
        <ManualAssignmentModal
          key={manualAssignmentModalOpen}
          open={manualAssignmentModalOpen}
          onClose={() => setManualAssignmentModalOpen(false)}
          onSave={updateAssignment}
          isSaving={isUpdatingAssignment}
          initialAssignment={assignment}
          grades={grades}
        />
      )}
    </Wrapper>
  );
};

AssignmentDetails.propTypes = {
  assignment: AssignmentType.isRequired,
  launchTurnitinAssignment: PropTypes.func,
  launchTurnitinSubmission: PropTypes.func,
  updateAssignment: PropTypes.func,
  isUpdatingAssignment: PropTypes.bool,
  deleteAssignment: PropTypes.func,
  importManualAssignmentGrades: PropTypes.func,
  spaceId: PropTypes.string.isRequired,
  grades: PropTypes.arrayOf(GradesPropType),
  isUploadingGrades: PropTypes.bool,
  viewAs: TYPES.ViewAs,
  ltiProvider: TYPES.LtiProvider,
};

AssignmentDetails.defaultProps = {
  launchTurnitinAssignment: () => {},
  updateAssignment: () => {},
  isUpdatingAssignment: false,
  deleteAssignment: () => {},
  importManualAssignmentGrades: () => {},
  grades: [],
  isUploadingGrades: false,
  viewAs: EDUCATOR,
  ltiProvider: null,
};

export default AssignmentDetails;
